"use strict";

import React, { useState } from "react";
import { Button } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import {
  attachSubtitles,
  isSrtFile,
  uploadSubtitleToStorage,
} from "./uploadSubtitle.js";

/**
 * Picks one SRT and attaches it to the project in a single step, for the
 * given role ("original" or "translated") and language.
 */
const SrtUploadButton = ({
  projectId,
  role,
  languageCode,
  disabled,
  onAttached,
  children = "Upload SRT",
}) => {
  const [progress, setProgress] = useState(null);
  const isUploading = progress !== null;

  const handleFile = async (file) => {
    if (!file) {
      return;
    }
    if (!isSrtFile(file)) {
      toast.error("Subtitle files must use the .srt extension");
      return;
    }

    setProgress(0);
    try {
      const objectName = await uploadSubtitleToStorage({
        projectId,
        id: uuidv4(),
        file,
        onProgress: setProgress,
      });
      const copiedSubtitles = await attachSubtitles(projectId, [
        { role, languageCode, fileName: file.name, objectName },
      ]);
      toast.success(`${file.name} uploaded`);
      onAttached?.(copiedSubtitles);
    } catch (error) {
      console.error("Error uploading subtitle file:", error);
      toast.error(
        error.response?.data?.message || error.message || "Error uploading subtitle file"
      );
    } finally {
      setProgress(null);
    }
  };

  return (
    <Button
      component="label"
      size="small"
      variant="outlined"
      startIcon={<UploadFileIcon />}
      disabled={disabled || isUploading}
    >
      {isUploading ? `Uploading ${Math.round(progress)}%` : children}
      <input
        hidden
        type="file"
        accept=".srt,application/x-subrip,text/plain"
        onChange={(event) => {
          handleFile(event.target.files?.[0]);
          event.target.value = "";
        }}
      />
    </Button>
  );
};

export default SrtUploadButton;
